import { useEffect, useState } from "react";
import api, { asItems } from "@/lib/api";
import { money, dt, INVOICE_LABELS, INVOICE_COLORS, TIPO_COMPROBANTE_LABELS } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, RefreshCw, Ban, Send, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Invoicing() {
  const [facturas, setFacturas] = useState(null);
  const [estadoFiscal, setEstadoFiscal] = useState(null);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(null);

  const load = async () => {
    const [f, e] = await Promise.all([api.get("/facturas"), api.get("/facturas/estado")]);
    setFacturas(f.data);
    setEstadoFiscal(e.data);
  };
  useEffect(() => { load(); }, []);

  const openForm = async () => {
    if (orders.length === 0) {
      const r = await api.get("/orders", { params: { estado: "pagado", limit: 200 } });
      setOrders(asItems(r));
    }
    setForm({ order_id: "", tipo_comprobante: "consumo_final", rnc_cedula: "", razon_social: "" });
  };

  const crear = async () => {
    if (!form.order_id) return toast.error("Elegí un pedido");
    try {
      await api.post("/facturas", form);
      toast.success("Factura encolada para emisión");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const reintentar = async (f) => {
    try { await api.post(`/facturas/${f.id}/reintentar`); toast.success("Reintento encolado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const anular = async (f) => {
    try { await api.post(`/facturas/${f.id}/anular`); toast.success("Factura anulada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const enviar = async (f) => {
    try { await api.post(`/facturas/${f.id}/enviar`); toast.success("Comprobante enviado al cliente"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "El pedido no tiene conversación asociada"); }
  };
  const verComprobante = (f) => {
    window.open(`${window.location.origin}/comprobante/${f.id}`, "_blank");
  };

  const grupos = {
    pendiente: facturas?.filter((f) => f.estado === "pendiente") || [],
    emitida: facturas?.filter((f) => f.estado === "emitida") || [],
    error: facturas?.filter((f) => f.estado === "error") || [],
    anulada: facturas?.filter((f) => f.estado === "anulada") || [],
  };

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Facturación DGII" subtitle="Comprobantes fiscales electrónicos (e-CF).">
        <Button onClick={openForm} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="nueva-factura-button"><Plus className="h-4 w-4" /> Nueva factura</Button>
      </PageHeader>

      {estadoFiscal && (
        <div className={`ms-card p-4 mb-4 flex items-center gap-3 ${estadoFiscal.configurado ? "" : "bg-amber-50"}`} data-testid="fiscal-estado-banner">
          {!estadoFiscal.configurado && <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />}
          <div className="text-sm">
            <span className="font-medium">Proveedor: {estadoFiscal.modo_proveedor === "simulado" ? "Simulado (demo)" : "Real"}</span>
            {" · "}
            <span className="text-muted-foreground">Emisión automática: {estadoFiscal.activo ? "activada" : "desactivada"}</span>
            {!estadoFiscal.configurado && <span className="text-amber-600"> · Pendiente de conexión (faltan credenciales del proveedor real)</span>}
            {estadoFiscal.es_demo && <span className="text-amber-600"> · Los comprobantes emitidos en modo demo NO son válidos ante la DGII</span>}
          </div>
        </div>
      )}

      {!facturas ? <Loader /> : facturas.length === 0 ? (
        <EmptyState icon={FileText} title="Sin facturas todavía" subtitle="Se generan automáticamente cuando un pedido pasa a pagado (si la emisión automática está activa) o manualmente desde acá." />
      ) : (
        <Tabs defaultValue="pendiente">
          <TabsList className="rounded-full mb-4 flex-wrap h-auto">
            <TabsTrigger value="pendiente" className="rounded-full" data-testid="tab-fact-pendiente">Pendientes ({grupos.pendiente.length})</TabsTrigger>
            <TabsTrigger value="emitida" className="rounded-full" data-testid="tab-fact-emitida">Emitidas ({grupos.emitida.length})</TabsTrigger>
            <TabsTrigger value="error" className="rounded-full" data-testid="tab-fact-error">Errores ({grupos.error.length})</TabsTrigger>
            <TabsTrigger value="anulada" className="rounded-full" data-testid="tab-fact-anulada">Anuladas ({grupos.anulada.length})</TabsTrigger>
          </TabsList>
          {Object.entries(grupos).map(([k, list]) => (
            <TabsContent key={k} value={k}>
              {list.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">Nada acá.</p> : (
                <div className="space-y-2">
                  {list.map((f) => (
                    <div key={f.id} className="ms-card p-4 flex flex-wrap items-center gap-3" data-testid={`factura-${f.id}`}>
                      <div className="flex-1 min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <span className="font-head font-semibold">Pedido #{f.numero_pedido}</span>
                          <Badge className={`rounded-full border-0 ${INVOICE_COLORS[f.estado]}`}>{INVOICE_LABELS[f.estado]}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{TIPO_COMPROBANTE_LABELS[f.tipo_comprobante]} {f.ncf ? `· NCF ${f.ncf}` : ""}</p>
                        {f.error_detalle && <p className="text-xs text-red-500 mt-0.5">{f.error_detalle}</p>}
                        <p className="text-xs text-muted-foreground mt-0.5">{dt(f.created_at)}</p>
                      </div>
                      <span className="font-head font-semibold text-primary">{money(f.monto_total)}</span>
                      <div className="flex gap-1">
                        {f.estado === "error" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => reintentar(f)} data-testid={`reintentar-${f.id}`}><RefreshCw className="h-4 w-4" /></Button>}
                        {f.estado === "emitida" && (
                          <>
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => verComprobante(f)} data-testid={`ver-${f.id}`}><ExternalLink className="h-4 w-4" /></Button>
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => enviar(f)} data-testid={`enviar-${f.id}`}><Send className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => anular(f)} data-testid={`anular-${f.id}`}><Ban className="h-4 w-4" /></Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nueva factura</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div>
                <Label>Pedido (pagados)</Label>
                <Select value={form.order_id} onValueChange={(v) => setForm({ ...form, order_id: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="factura-order-select"><SelectValue placeholder="Elegí un pedido…" /></SelectTrigger>
                  <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>#{o.numero} · {o.cliente_nombre} · {money(o.total)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de comprobante</Label>
                <Select value={form.tipo_comprobante} onValueChange={(v) => setForm({ ...form, tipo_comprobante: v })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="factura-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consumo_final">{TIPO_COMPROBANTE_LABELS.consumo_final}</SelectItem>
                    <SelectItem value="credito_fiscal">{TIPO_COMPROBANTE_LABELS.credito_fiscal}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.tipo_comprobante === "credito_fiscal" && (
                <>
                  <div><Label>RNC/Cédula</Label><Input value={form.rnc_cedula} onChange={(e) => setForm({ ...form, rnc_cedula: e.target.value })} className="rounded-xl mt-1" data-testid="factura-rnc" /></div>
                  <div><Label>Razón social</Label><Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} className="rounded-xl mt-1" data-testid="factura-razon-social" /></div>
                </>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setForm(null)}>Cancelar</Button><Button onClick={crear} className="rounded-full bg-primary hover:bg-primary/90" data-testid="factura-crear-submit">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
